class ProductRecommendations extends HTMLElement {
  static SCROLL_TOLERANCE = 2;
  static MIN_LINE_THUMB_WIDTH = 14;

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Cleanup function for scroll controls.
   * @type {(() => void) | null}
   */
  #controlsCleanup = null;

  connectedCallback() {
    const shouldLoadImmediately =
      this.dataset.intent === 'complementary' && this.dataset.recommendationsPerformed !== 'true';

    if (shouldLoadImmediately) {
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }

    this.#mutationObserver.observe(this, { attributes: true });
    this.#disableMobileCardGallerySlideshows();
    this.#setupScrollControls();
  }

  disconnectedCallback() {
    this.#cleanupScrollControls();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.hidden = false;
          this.classList.remove('hidden');
          this.innerHTML = recommendations.innerHTML;
          this.#disableMobileCardGallerySlideshows();
          this.#setupScrollControls();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = this.#getRecommendationsUrl(productId, sectionId, intent);

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Builds the Product Recommendations API URL.
   * New markup includes the full documented query string in data-url. The
   * fallback keeps older rendered markup working if a section is hot-swapped.
   *
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {string}
   */
  #getRecommendationsUrl(productId, sectionId, intent) {
    const baseUrl = this.dataset.url || '';

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      if (sectionId) url.searchParams.set('section_id', sectionId);
      if (intent) url.searchParams.set('intent', intent);
      return `${url.pathname}${url.search}`;
    } catch (_error) {
      const query = new URLSearchParams();
      query.set('product_id', productId);
      if (sectionId) query.set('section_id', sectionId);
      if (intent) query.set('intent', intent);

      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${query.toString()}`;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.hidden = true;
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /** @returns {void} */
  #cleanupScrollControls() {
    if (typeof this.#controlsCleanup === 'function') {
      this.#controlsCleanup();
    }
    this.#controlsCleanup = null;
  }

  /** @returns {void} */
  #disableMobileCardGallerySlideshows() {
    this.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
      slideshow.setAttribute('mobile-disabled', '');
    });
  }

  /** @returns {HTMLElement | null} */
  #getVisibleScroller() {
    const scrollers = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.resource-list__carousel slideshow-slides')
    );
    for (const scroller of scrollers) {
      if (!(scroller instanceof HTMLElement)) continue;
      if (scroller.offsetParent === null) continue;
      return scroller;
    }
    return null;
  }

  /**
   * @param {HTMLElement} scroller
   * @returns {number}
   */
  #getScrollStep(scroller) {
    const firstSlide = scroller.querySelector('.resource-list__slide');
    if (firstSlide instanceof HTMLElement) {
      const styles = window.getComputedStyle(scroller);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return firstSlide.getBoundingClientRect().width + gap;
    }

    return Math.max(scroller.clientWidth * 0.85, 1);
  }

  #setupScrollControls() {
    this.#cleanupScrollControls();

    const controls = this.querySelector('[data-pr-controls]');
    const progressLine = this.querySelector('[data-pr-line]');
    const progressThumb = this.querySelector('[data-pr-line-thumb]');
    const prevButton = this.querySelector('[data-pr-prev]');
    const nextButton = this.querySelector('[data-pr-next]');

    if (!(controls instanceof HTMLElement)) return;
    if (!(progressLine instanceof HTMLElement)) return;
    if (!(progressThumb instanceof HTMLElement)) return;
    if (!(prevButton instanceof HTMLButtonElement)) return;
    if (!(nextButton instanceof HTMLButtonElement)) return;

    const updateControls = () => {
      const scroller = this.#getVisibleScroller();
      if (!(scroller instanceof HTMLElement)) {
        controls.hidden = true;
        return;
      }

      const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const hasOverflow = maxScrollLeft > ProductRecommendations.SCROLL_TOLERANCE;
      controls.hidden = !hasOverflow;

      const lineWidth = progressLine.getBoundingClientRect().width || 74;
      if (!hasOverflow) {
        prevButton.disabled = true;
        nextButton.disabled = true;
        progressThumb.style.setProperty('--pr-line-thumb-width', `${lineWidth}px`);
        progressThumb.style.setProperty('--pr-line-thumb-translate', '0px');
        return;
      }

      const progress = maxScrollLeft > 0 ? scroller.scrollLeft / maxScrollLeft : 0;
      const thumbWidth = Math.max(
        Math.min((scroller.clientWidth / scroller.scrollWidth) * lineWidth, lineWidth),
        ProductRecommendations.MIN_LINE_THUMB_WIDTH
      );
      const thumbTravel = Math.max(lineWidth - thumbWidth, 0);

      progressThumb.style.setProperty('--pr-line-thumb-width', `${thumbWidth}px`);
      progressThumb.style.setProperty('--pr-line-thumb-translate', `${thumbTravel * progress}px`);

      prevButton.disabled = scroller.scrollLeft <= ProductRecommendations.SCROLL_TOLERANCE;
      nextButton.disabled = scroller.scrollLeft >= maxScrollLeft - ProductRecommendations.SCROLL_TOLERANCE;
    };

    const onScroll = () => {
      updateControls();
    };

    const onPrev = () => {
      const scroller = this.#getVisibleScroller();
      if (!(scroller instanceof HTMLElement)) return;
      scroller.scrollBy({ left: -this.#getScrollStep(scroller), behavior: 'smooth' });
    };

    const onNext = () => {
      const scroller = this.#getVisibleScroller();
      if (!(scroller instanceof HTMLElement)) return;
      scroller.scrollBy({ left: this.#getScrollStep(scroller), behavior: 'smooth' });
    };

    prevButton.addEventListener('click', onPrev);
    nextButton.addEventListener('click', onNext);
    window.addEventListener('resize', updateControls);

    const scrollers = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.resource-list__carousel slideshow-slides')
    );
    scrollers.forEach((scroller) => {
      if (scroller instanceof HTMLElement) {
        scroller.addEventListener('scroll', onScroll, { passive: true });
      }
    });

    updateControls();

    this.#controlsCleanup = () => {
      prevButton.removeEventListener('click', onPrev);
      nextButton.removeEventListener('click', onNext);
      window.removeEventListener('resize', updateControls);
      scrollers.forEach((scroller) => {
        if (scroller instanceof HTMLElement) {
          scroller.removeEventListener('scroll', onScroll);
        }
      });
    };
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
